/**
 * Reusable client-side pagination control + helper hook.
 *
 * Pattern across the site:
 *   const { page, pageSize, setPage, setPageSize, totalPages, slice } =
 *     usePagination(filtered, { defaultPageSize: 50, resetKey: filters });
 *   const visible = slice(filtered);
 *   ...
 *   <Pagination
 *     page={page}
 *     totalPages={totalPages}
 *     pageSize={pageSize}
 *     totalItems={filtered.length}
 *     onPageChange={setPage}
 *     onPageSizeChange={setPageSize}
 *   />
 *
 * Why client-side: existing list endpoints already return the full filtered
 * dataset and components do their own filtering/sorting in memo. Slicing the
 * rendered output is enough to stop the UI lock-up from rendering 1000+ rows
 * (each with 3 <select> dropdowns). Server-side paging would require API
 * changes; we can layer that on later behind the same hook surface.
 */

import { useEffect, useMemo, useState } from 'react';

const DEFAULT_PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;

interface UsePaginationOptions {
  defaultPageSize?: number;
  pageSizeOptions?: readonly number[];
  /** Any value that, when changed, should reset the page back to 1 (e.g. filters). */
  resetKey?: unknown;
  /** Optional localStorage key — persists the page size choice across sessions. */
  storageKey?: string;
}

export interface PaginationState<T> {
  page: number;
  pageSize: number;
  totalPages: number;
  setPage: (next: number | ((prev: number) => number)) => void;
  setPageSize: (next: number) => void;
  /** Returns `items.slice(start, end)` for the current page. */
  slice: (items: readonly T[]) => T[];
  pageSizeOptions: readonly number[];
}

function readStoredPageSize(storageKey: string | undefined, fallback: number): number {
  if (!storageKey || typeof window === 'undefined') return fallback;
  const raw = window.localStorage.getItem(storageKey);
  if (!raw) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function usePagination<T>(
  items: readonly T[],
  opts: UsePaginationOptions = {}
): PaginationState<T> {
  const {
    defaultPageSize = 50,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    resetKey,
    storageKey,
  } = opts;

  const [pageSize, setPageSizeRaw] = useState<number>(
    () => readStoredPageSize(storageKey, defaultPageSize)
  );
  const [page, setPage] = useState(1);

  // Persist page-size choice when a storageKey is supplied.
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    window.localStorage.setItem(storageKey, String(pageSize));
  }, [storageKey, pageSize]);

  // Reset to page 1 whenever the consumer-supplied resetKey changes
  // (typically a filters object) OR when pageSize changes.
  useEffect(() => { setPage(1); }, [resetKey, pageSize]);

  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));

  // Keep page within valid range — important when filters shrink the dataset.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const safePage = Math.min(page, totalPages);

  const setPageSize = (next: number) => {
    setPageSizeRaw(Math.max(1, Math.floor(next)));
  };

  const slice = useMemo(
    () => (input: readonly T[]) => input.slice((safePage - 1) * pageSize, safePage * pageSize),
    [safePage, pageSize]
  );

  return {
    page: safePage,
    pageSize,
    totalPages,
    setPage,
    setPageSize,
    slice,
    pageSizeOptions,
  };
}

interface PaginationProps {
  page: number;
  totalPages: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (next: number) => void;
  onPageSizeChange?: (next: number) => void;
  pageSizeOptions?: readonly number[];
  /** ARIA label for the navigation landmark. */
  ariaLabel?: string;
  /** Item noun for the summary (e.g. "events"). */
  itemLabel?: string;
}

export function Pagination({
  page,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  ariaLabel = 'Pagination',
  itemLabel = 'items',
}: PaginationProps) {
  // Hide the entire control when there's nothing to paginate AND no page-size
  // selector — it's just visual noise on an empty list.
  if (totalItems === 0 && !onPageSizeChange) return null;

  const start = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalItems);
  const onlyOnePage = totalPages <= 1;

  return (
    <nav
      aria-label={ariaLabel}
      className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3"
    >
      <div className="small text-muted">
        {totalItems === 0
          ? `No ${itemLabel}`
          : `Showing ${start.toLocaleString()}–${end.toLocaleString()} of ${totalItems.toLocaleString()} ${itemLabel}`}
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2">
        {onPageSizeChange && (
          <label className="d-inline-flex align-items-center gap-1 small text-muted mb-0">
            <span>Per page</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              aria-label="Items per page"
            >
              {pageSizeOptions.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        )}

        {!onlyOnePage && (
          <>
            <span className="small text-muted" aria-live="polite">
              Page {page} of {totalPages}
            </span>
            <div className="btn-group btn-group-sm" role="group" aria-label="Page navigation">
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page <= 1}
                onClick={() => onPageChange(1)}
                aria-label="First page"
              >
                « First
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page <= 1}
                onClick={() => onPageChange(Math.max(1, page - 1))}
                aria-label="Previous page"
              >
                ‹ Prev
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page >= totalPages}
                onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                aria-label="Next page"
              >
                Next ›
              </button>
              <button
                type="button"
                className="btn btn-outline-secondary"
                disabled={page >= totalPages}
                onClick={() => onPageChange(totalPages)}
                aria-label="Last page"
              >
                Last »
              </button>
            </div>
          </>
        )}
      </div>
    </nav>
  );
}

export default Pagination;
